import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { Upgrade } from 'src/app/_models/upgrade';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { toDataSourceRequestString, translateDataSourceResultGroups } from '@progress/kendo-data-query';
import { map, tap } from 'rxjs/operators';
import { ImportAsset } from 'src/app/_models/importAsset';
import { Asset } from 'src/app/_models/asset';
import { RejectReason } from 'src/app/_models/rejectReason';

@Injectable({
   providedIn: 'root',
})
export class ImportAssetService extends BehaviorSubject<GridDataResult> {
   tableName = 'ImportAsset';
   public param1: string;
   public loading: boolean;
   public isTemplateOnly: boolean;
   private BASE_URL = environment.apiUrl;
   baseUrl = environment.apiUrl;
   importSelectedSplit = new Subject<ImportAsset>();
   importSelectedChangeDate = new Subject<ImportAsset>();
   importSelectedChangeAccount = new Subject<ImportAsset>();
   importSelectedImportLog = new Subject<ImportAsset>();
   refreshTrigger = new Subject<string>();
   constructor(private http: HttpClient) {
      super(null);
   }

   // Subscription States
   changeStateForSplit(importAsset: ImportAsset) {
      this.importSelectedSplit.next(importAsset);
   }

   changeStateForChangeDate(importAsset: ImportAsset) {
      this.importSelectedChangeDate.next(importAsset);
   }

   changeStateForChangeAccount(importAsset: ImportAsset) {
      this.importSelectedChangeAccount.next(importAsset);
   }

   changeStateForImportLog(importAsset: ImportAsset) {
      this.importSelectedImportLog.next(importAsset);
   }
   // API Calls
   rejectReview(id: Number, reason: RejectReason) {
      return this.http.put(this.baseUrl + 'ImportAsset/RejectReview/' + id, reason);
   }

   skipReview(id: Number) {
      return this.http.put(this.baseUrl + 'ImportAsset/SkipReview/' + id, null);
   }

   getAssetImport(id: Number): Observable<ImportAsset> {
      return this.http.get<ImportAsset>(this.baseUrl + 'ImportAsset/' + id);
   }

   splitImport(importAsset: ImportAsset) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/SplitImport', importAsset);
   }

   changeReadyDate(importAsset: ImportAsset, changed: boolean) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/ChangeReadyDate/' + changed, importAsset);
   }

   changeAccount(importAsset: ImportAsset) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/ChangeAccount', importAsset);
   }

   processUpgrade(id: number, upgrade: Upgrade) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/ProcessUpgrade/' + id, upgrade);
   }

   processAsset(id: number, asset: Asset) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/ProcessAsset/' + id, asset);
   }

   processWIP(id: number) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/ProcessWIP/' + id, null);
   }

   unarchiveReview(id: Number) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/Unarchive/' + id, null);
   }
   addInvoiceToArchive(id: Number) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/AddInvoiceToArchive/' + id, null);
   }

   addInvoiceToReview(id: Number) {
      return this.http.post<ImportAsset>(this.baseUrl + 'ImportAsset/AddInvoiceToReview/' + id, null);
   }

   markAsProcessed(id: Number, upgradepara: ImportAsset) {
      return this.http.put(this.baseUrl + 'ImportAsset/MarkAsProcessed/' + id, upgradepara);
   }

   updateImpAssetIsUpgrade(id: Number, upgradepara: ImportAsset) {
      return this.http.put(this.baseUrl + 'ImportAsset/UpdateImpAssetToIsUpgrade/' + id, upgradepara);
   }

   updateImpAssetIsDiscard(id: Number, discardpara: ImportAsset) {
      return this.http.put(this.baseUrl + 'ImportAsset/UpdateImpAssetToIsDiscard/' + id, discardpara);
   }

   updateImpAssetToReinstate(id: Number, discardpara: ImportAsset) {
      return this.http.put(this.baseUrl + 'ImportAsset/UpdateImpAssetToReinstate/' + id, discardpara);
   }
   exportAsset(WithAcq: boolean, isImpAssetERP: boolean): Observable<Blob> {
      // tslint:disable-next-line:max-line-length

      this.isTemplateOnly = false;
      return this.http.get(this.baseUrl + 'asset/export/' + WithAcq + '/' + isImpAssetERP, {
         responseType: 'blob',
      });
   }

   exporUpgrade(): Observable<Blob> {
      // tslint:disable-next-line:max-line-length
      this.isTemplateOnly = false;
      return this.http.get(this.baseUrl + 'Upgrade/export/' + this.isTemplateOnly, {
         responseType: 'blob',
      });
   }

   exportDiscard(isReinstate: boolean): Observable<Blob> {
      // tslint:disable-next-line:max-line-length
      this.isTemplateOnly = false;
      return this.http.get(this.baseUrl + 'ImportAsset/exportDiscard/' + isReinstate, {
         responseType: 'blob',
      });
   }

   exportWip(OnlyTemplate: boolean, isImpAssetErp: boolean): Observable<Blob> {
      // tslint:disable-next-line:max-line-length
      return this.http.get(this.baseUrl + 'transaction/export/' + OnlyTemplate + '/' + isImpAssetErp, {
         responseType: 'blob',
      });
   }

   // Directive Methods
   public query(state: any): void {
      this.fetch(this.tableName, this.param1, state).subscribe((x) => super.next(x));
   }

   protected fetch(tableName: string, param1: string, state: any): Observable<GridDataResult> {
      const queryStr = `${toDataSourceRequestString(state)}`; // Serialize the state
      const hasGroups = state.group && state.group.length;
      this.loading = true;
      let url = ``;
      url = `${this.BASE_URL}${'ImportAsset/ImportLog'}?id=${param1}&${queryStr}`;

      return this.http.get(url).pipe(
         map(
            (
               { data, total /*, aggregateResults*/ }: GridDataResult // Process the response
            ) =>
               <GridDataResult>{
                  // If there are groups, convert them to a compatible format
                  data: hasGroups ? translateDataSourceResultGroups(data) : data,
                  total: total,
               }
         ),
         tap(() => (this.loading = false))
      );
   }
}
